
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useCampaignLogs } from "@/hooks/campaigns/useCampaignLogs";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface CampaignAuditLogProps {
  campaignId: string;
}

const CampaignAuditLog = ({ campaignId }: CampaignAuditLogProps) => {
  const { data: logs = [], isLoading } = useCampaignLogs(campaignId);

  const getActionBadgeVariant = (actionType: string) => {
    switch (actionType) {
      case 'created':
        return 'default';
      case 'updated':
        return 'secondary';
      case 'activated':
      case 'rules_applied':
        return 'default';
      case 'completed':
      case 'rules_reverted':
        return 'outline';
      case 'deleted':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getActionLabel = (actionType: string) => {
    const labels: Record<string, string> = {
      created: 'Criada',
      updated: 'Atualizada',
      activated: 'Ativada',
      completed: 'Finalizada',
      deleted: 'Excluída',
      rules_applied: 'Regras Aplicadas',
      rules_reverted: 'Regras Revertidas',
    };
    return labels[actionType] || actionType;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Log de Auditoria</CardTitle>
          <CardDescription>Histórico de alterações da campanha</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">Carregando...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Log de Auditoria</CardTitle>
        <CardDescription>Histórico de alterações da campanha</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[300px]">
          {logs.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              Nenhum log encontrado
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => (
                <div key={log.id} className="flex items-start space-x-3 pb-3 border-b last:border-b-0">
                  <Badge variant={getActionBadgeVariant(log.action_type)}>
                    {getActionLabel(log.action_type)}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                      {log.description || `Campanha ${getActionLabel(log.action_type).toLowerCase()}`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(log.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default CampaignAuditLog;
